// ABOUTME: Turns realtime events into a refresh of the paths the calendar already uses to load —
// ABOUTME: the window prefetch and the coach screen's chunk fetchers — throttled and coalesced.
import type { ApolloCache } from "@apollo/client"
import type { ModifierDetails } from "@apollo/client/cache"
import type { Reference, StoreObject } from "@apollo/client/utilities"
import { createLogger } from "@/model/logger"
import type { RealtimeEvent } from "@/model/realtime/events"
import { refetchBatchChunks } from "@/model/calendar/batchChunkRefetch"
import { isOwned } from "@/model/calendar/cache/recordOwners"
import { reloadWindowIfTouched } from "@/model/calendar/calendarWindowLoader"
import type { CalendarRange } from "@/model/calendar/cache/calendarRange"
import { resetRunSerialForTests, runSerial } from "@/model/calendar/cache/runSerial"
import { dateSpan } from "@/model/date"
import { refreshStartsIfTouched } from "@/model/competitions/startsRefresh"

const logger = createLogger("Realtime")

/**
 * The days a flush of events says changed, per family. `null` stands for
 * "everything": a channel that could not replay what was missed, and the
 * visibility transition at midnight UTC, which no event announces.
 */
export interface CalendarChanges {
  calendar: readonly CalendarRange[]
  health: readonly CalendarRange[]
}

/**
 * A burst of events costs one refresh, and a refresh asked for inside the
 * window runs at its end rather than being dropped — the same "defer, never
 * drop" rule the session's reload uses. Five seconds is short
 * enough that a coach editing an athlete's day sees it, and long enough that
 * the morning's watch webhooks land as one refresh.
 */
export const REFRESH_MIN_INTERVAL_MS = 5000

const REFRESH_KEY = "calendar-refresh"

/**
 * What this does NOT do, on purpose: it does not correct a cached entry that
 * nobody is showing. Such an entry is refreshed when its view next reads it —
 * which is what the five-minute poll did before the channel existed, so the
 * channel only makes the same refresh happen in a second instead of in five
 * minutes. Deciding otherwise means modelling which cache keys exist, who owns
 * them and whose answer is newer, and that is the bug surface this replaced.
 */
export function changesFromEvents(events: readonly RealtimeEvent[]): CalendarChanges {
  const calendar: CalendarRange[] = []
  const health: CalendarRange[] = []
  for (const event of events) {
    // A message changes no day of the calendar: its badge is the discussion's own business.
    if (event.type === "discussion.changed") continue
    const range = { userId: event.userId, from: event.from, to: event.to }
    if (event.type === "health.changed") health.push(range)
    else calendar.push(range)
  }
  return { calendar, health }
}

const merge = (left: CalendarChanges | null, right: CalendarChanges | null): CalendarChanges | null =>
  left === null || right === null
    ? null
    : { calendar: [...left.calendar, ...right.calendar], health: [...left.health, ...right.health] }

const logRejection = (what: string) => (error: unknown) => logger.warn(`${what} failed`, { error })

/**
 * Each path refuses the work that is not its own: the window compares the
 * ranges with the weeks and owner it shows, the chunk fetchers with their
 * athletes and dates. An event for somebody nobody is looking at reaches a
 * registry that answers at once, and no request is made.
 */
async function run(changes: CalendarChanges | null): Promise<void> {
  // The race countdowns and the season races: a race added, moved or removed on one of their days may change them. Not
  // awaited: Apollo's own hook publishes the answer, the starts reads are serialized on a key of their own, and one
  // that hangs must not hold the next refresh of the weeks back. It never rejects (runSerial).
  void refreshStartsIfTouched(changes === null ? null : changes.calendar)
  // The window prefetch fetches the health summary too, so both families reach it.
  const forWindow = changes === null ? null : [...changes.calendar, ...changes.health]
  await Promise.all([
    reloadWindowIfTouched(forWindow).catch(logRejection("Window refresh")),
    refetchBatchChunks("calendar", changes === null ? null : changes.calendar).catch(logRejection("Chunk refetch")),
    refetchBatchChunks("health", changes === null ? null : changes.health).catch(logRejection("Chunk refetch")),
  ])
}

/** Refreshes what is on screen. `null` means everything the channel feeds. */
export function refreshFromEvent(changes: CalendarChanges | null): Promise<void> {
  // The result is nobody's business here: a remote refresh logs its failures
  // and retries on the next event or tick.
  return runSerial<CalendarChanges | null, void>(REFRESH_KEY, changes, run, {
    merge,
    minIntervalMs: REFRESH_MIN_INTERVAL_MS,
  }).then(() => undefined)
}

/**
 * Root Query fields that hold a copy of what a day contains — every cached
 * view of the calendar, not only the ones some screen is showing.
 */
const CALENDAR_VIEW_FIELDS = [
  "calendarItems",
  "calendarItemsBatch",
  "calendarOverview",
  "workoutPeriodRecords",
  "basicRequirements",
  "basicRequirementsBatch",
  "healthMetrics",
]

/**
 * A LOCAL change — an import the user has just performed — drops every cached
 * view of the calendar and then refreshes what is on screen. A REMOTE event
 * does only the second half: it arrives while the user is looking at something
 * else entirely, and an evict there would send every mounted row back to the
 * network (the avalanche of 17.08.2026).
 *
 * The difference is what the user expects. After importing a workout they may
 * open statistics, which reads `calendarItems` under its own arguments and is
 * a separate record no on-screen refresh reaches; it must not still be missing
 * the workout they just imported. The cost is bounded by where this runs: the
 * MANUAL imports, in settings, where none of the evicted records is mounted.
 * A background import must not call this — it fires while the user is looking
 * at the calendar and the evict would send every mounted week and row back to
 * the network (useLactateWorkoutImport passes isBackground for exactly that).
 */
export function refreshAfterLocalChange(cache: ApolloCache, changes: CalendarChanges): Promise<void> {
  for (const fieldName of CALENDAR_VIEW_FIELDS) cache.evict({ id: "ROOT_QUERY", fieldName })
  // Collection belongs here, unlike after a single eviction: whole root fields
  // have just gone, and what they referenced would stay for the session. What
  // it must not take is a record waiting out an undo window — those are held
  // for its length, see cache/retain.ts.
  cache.gc()
  // `run`, not `refreshFromEvent`: the five-second throttle collapses bursts of
  // REMOTE events, and what the user just did must show at once. Ordering is
  // not lost by skipping it — the window loader and the chunk runners are what
  // actually serialize these records, and this refresh goes through them.
  return run(changes)
}

/** `YYYY-MM-DD` anywhere in a stored field's key, including the date part of an ISO instant. */
const DATE_IN_KEY = /\d{4}-\d{2}-\d{2}/g

/** The athlete a stored field's key names: `{"userID":"…"}` or the `…__` prefix calendarItemsKeyArgs builds. */
const OWNER_IN_ARGS = /"userID":"([^"]+)"/
const OWNER_IN_KEY = /^[^:(]+:([^_(]+)__/

/**
 * Whether this entry belongs to somebody else. A key that names nobody is the
 * signed-in user's own: several queries omit `userID` for their own data
 * (health metrics), and calendarItemsKeyArgs writes the literal `unknown` for a
 * workout opened by link without an athlete parameter. Skipping those would
 * leave exactly the entries the importing user is looking at unrefreshed.
 */
function belongsToAnother(storeFieldName: string, userId: string): boolean {
  const owner = OWNER_IN_ARGS.exec(storeFieldName)?.[1] ?? OWNER_IN_KEY.exec(storeFieldName)?.[1]
  return owner !== undefined && owner !== userId && owner !== "unknown"
}

/**
 * Whether a cached entry can be showing one of these days. Three shapes of key
 * exist, and only the first one names its days outright:
 *
 * - by dates — the week's `calendarItems`, the per-day requirements: every day
 *   it holds is in the key;
 * - by range — `calendarOverview`, `workoutPeriodRecords`, `healthMetrics`:
 *   only the bounds are, and the changed day sits between them;
 * - by id — the open workout or menu editor (`calendarItems:me__ids_w1`, see
 *   calendarItemsKeyArgs): no date at all, so the records themselves are asked.
 *   This is the copy the editor on screen reads.
 */
function holdsAnyDay(
  storeFieldName: string,
  value: unknown,
  days: readonly string[],
  readField: ModifierDetails["readField"],
): boolean {
  const inKey: string[] = storeFieldName.match(DATE_IN_KEY) ?? []
  if (inKey.length > 0) {
    if (days.some((day) => inKey.includes(day))) return true
    const from = inKey.reduce((earliest, date) => (date < earliest ? date : earliest))
    const to = inKey.reduce((latest, date) => (date > latest ? date : latest))
    return days.some((day) => day >= from && day <= to)
  }
  if (typeof value !== "object" || value === null) return false
  const records = readField("records", value as StoreObject)
  if (!Array.isArray(records)) return false
  return records.some((record: StoreObject | Reference) => {
    const date = readField<string>("date", record)
    return typeof date === "string" && days.includes(date)
  })
}

/**
 * Whether an owner rewrites this exact entry — the weekly records a window or a
 * chunk fetcher writes. Everything else keyed by day (`basicRequirements(day)`),
 * by range (a statistics window) or by id (the open editor's copy) is refreshed
 * by nobody and has to be dropped.
 */
function rewrittenByOwner(storeFieldName: string, userId: string): boolean {
  const dates: string[] = storeFieldName.match(DATE_IN_KEY) ?? []
  // Fewer than a week's worth of dates is not a weekly entry; an ISO range
  // names only its two bounds and is nobody's either.
  if (dates.length < 7) return false
  const owner = OWNER_IN_ARGS.exec(storeFieldName)?.[1] ?? OWNER_IN_KEY.exec(storeFieldName)?.[1]
  const [from, to] = dateSpan(dates)
  return isOwned({
    userId: owner === undefined || owner === "unknown" ? userId : owner,
    from,
    to,
    dates,
  })
}

/**
 * Drops the cached views that can be showing one of these days FOR THIS
 * ATHLETE, and only those.
 * The whole calendar is dropped for a change the user made in front of the
 * screen (refreshAfterLocalChange); a BACKGROUND import — the lactate
 * auto-import, which runs app-wide — must not, or every mounted week and row
 * goes back to the network at once (the avalanche of 17.08.2026). But it must
 * not leave a cached-and-unmounted week, a statistics range, or an open
 * workout's by-id copy showing pre-import values either: nothing else refreshes
 * those, and reopening them would show the old numbers.
 */
export function evictDays(cache: ApolloCache, userId: string, days: readonly string[]): void {
  if (days.length === 0) return
  cache.modify({
    id: "ROOT_QUERY",
    fields: Object.fromEntries(
      CALENDAR_VIEW_FIELDS.map((fieldName) => [
        fieldName,
        (value: unknown, { storeFieldName, DELETE, readField }: ModifierDetails) => {
          // Somebody else's entries first: this runs app-wide, and a coach's
          // list holds a week per athlete under the same dates — dropping all
          // of them would send every mounted row back to the network, which is
          // the fan-out being avoided here in the first place.
          if (belongsToAnother(storeFieldName, userId)) return value
          if (!holdsAnyDay(storeFieldName, value, days, readField)) return value
          // THIS entry, not this day: an owner rewrites the weekly records and
          // nothing else, so the day's own query, a statistics range and the
          // by-id copy the editor reads are still ours to drop — they are
          // refreshed by nobody.
          return rewrittenByOwner(storeFieldName, userId) ? value : DELETE
        },
      ]),
    ),
  })
  cache.gc()
}

/** Test seam — the per-key state of the runner outlives a test file otherwise. */
export function resetRefreshCalendarForTests(): void {
  resetRunSerialForTests()
}
